import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Their tools, your broker',
    description:
      'DBAs connect with psql, DBeaver, or SSMS, straight to the Monopam proxy. The vault injects the real database credential; the user carries an opaque, short-lived handle.',
    bullets: [
      'Native clients: psql, DBeaver, SSMS, and more',
      'PostgreSQL, MySQL, and MSSQL wire protocols',
      'Real DB password injected by the proxy, never shown',
      'Short-lived, per-session proxy secrets',
    ],
  },
  {
    title: 'Policy that reads the SQL',
    description:
      'Rules evaluate the statement itself (commands, tables, patterns) and decide allow, block, or send to approval before the query reaches the database.',
    bullets: [
      'Command, table, and regex-based rules',
      'Require-WHERE and max-rows guardrails',
      'Work-hours and source-IP conditions',
      'Allow / block / approval per rule, risk-tiered',
    ],
  },
  {
    title: 'Masking for the data you must protect',
    description:
      'Dynamic data masking transforms sensitive columns on the way out; the DBA does their job, the customer data stays covered.',
    bullets: [
      'Dynamic masking with 8 transformer types',
      'PII auto-detection on result sets',
      'Turkish national ID, IBAN, and card transformers',
      'Per-user, per-policy masking decisions',
    ],
  },
  {
    title: 'An audit trail at the SQL level',
    description:
      'Regulators ask who ran what against production data. Monopam answers with the statement, the identity, and the timestamp, for years, not weeks.',
    bullets: [
      'SQL command-level audit logging',
      'Sessions tied to the human, not the shared account',
      'Long-term retention for banking-grade audits',
      'One trail across servers, clusters, and databases',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Vault the accounts',
    description:
      'Database credentials move into the vault with rotation. Shared DBA passwords stop circulating.',
  },
  {
    title: 'Write SQL policy',
    description:
      'Decide which statements run, which need approval, and which columns leave masked, per user, per database.',
  },
  {
    title: 'Broker every query',
    description:
      'Users connect through the proxy with their own tools. Every statement is evaluated, audited, and attributable.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Credential vault',
    description:
      'Rotation and brokered checkout for database accounts.',
    href: '/pam/vault',
  },
  {
    label: 'Use case: DevOps privileged access',
    description:
      'The full platform-team story: clusters to databases.',
    href: '/solutions/devops-privileged-access',
  },
  {
    label: 'What is DAM?',
    description:
      'Database access management, explained in the glossary.',
    href: '/glossary/database-access-management',
  },
]

export default function DatabaseAccessPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Database privileged access & dynamic masking"
        description="Privileged database access with native tools: proxy-injected credentials for PostgreSQL, MySQL, and MSSQL, SQL-level policy and audit, and dynamic data masking with PII auto-detection."
        path="/pam/database-access"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Database access"
          title={
            <>
              Production data,
              <br />
              without the shared password.
            </>
          }
          body="Database access management (DAM) the way your DBAs already work: they keep psql, DBeaver, and SSMS while Monopam brokers the connection. Credentials injected from the vault, every statement policy-checked and audited, sensitive columns masked on the way out."
          badges={[
            'PostgreSQL · MySQL · MSSQL',
            'SQL-level policy & audit',
            'Dynamic data masking',
          ]}
        />
        <SolutionPillars
          title={<>The database is where the data lives. Guard the door.</>}
          body="Server PAM stops at the shell. Monopam continues into the wire protocol, so access to the data itself is brokered, filtered, masked, and attributable."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to governed database access.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
